import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import qs from 'qs';
import makeStyles from '@mui/styles/makeStyles';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';

import { DatatablePreview, PreviewPanel } from 'ui';
import { LinkButton } from 'ui/Buttons';
import { fetchEntities } from 'services';
import { parseIndustryEntities } from 'shared/parsers';
import { IssuerDashboardResearchWidget } from '../IssuerDashboardResearchWidget/IssuerDashboardResearchWidget';

const useStyles = makeStyles({
  divider: {
    height: '100%',
  },
});

export const IndustryWidget = ({ industry, limit, ultimateIssuerId }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [isPending, setIsPending] = useState(false);
  const [entities, setEntities] = useState([]);
  const { t } = useTranslation();

  const getEntities = async () => {
    try {
      setIsPending(true);

      const { data } = await fetchEntities({
        page: 1,
        limit,
        filter: {
          industrySectors: [{ sector: industry.code, industry: industry.industryCode }],
          withRatingOnly: true,
        },
      });

      setEntities(parseIndustryEntities(data));
    } finally {
      setIsPending(false);
    }
  };

  useEffect(() => {
    getEntities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    {
      label: 'Name',
      options: {
        customBodyRender: (_value, { rowData: [[id, name]] }) => (
          <LinkButton to={`/entities/${id}`} label={name.toUpperCase()} />
        ),
      },
    },
    { label: t('Country') },
    { label: t('Rating type') },
    { label: t('Entity rating') },
    { label: t('Short-term debt rating') },
  ];

  const viewAllParams = {
    code: industry.code,
    industryCode: industry.industryCode,
  };

  return (
    <PreviewPanel title={industry.name} subtitle="Peer group credit ratings">
      <Grid container>
        <Grid item xs={6}>
          <Box display="flex">
            <DatatablePreview
              columns={columns}
              data={entities}
              loading={isPending}
              onViewAllClick={() =>
                navigate(`/credit-rating-coverage/industry?${qs.stringify(viewAllParams)}`)
              }
            />
            <Box ml={2} pb={2} height="auto">
              <Divider orientation="vertical" className={classes.divider} />
            </Box>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box px={2}>
            <IssuerDashboardResearchWidget
              sector={industry.code}
              industry={industry.industryCode}
              limit={5}
              id={ultimateIssuerId}
            />
          </Box>
        </Grid>
      </Grid>
    </PreviewPanel>
  );
};

IndustryWidget.propTypes = {
  limit: PropTypes.number.isRequired,
  industry: PropTypes.shape({
    name: PropTypes.string.isRequired,
    code: PropTypes.string.isRequired,
    industryName: PropTypes.string.isRequired,
    industryCode: PropTypes.string.isRequired,
  }).isRequired,
  ultimateIssuerId: PropTypes.string.isRequired,
};
